import React, { PureComponent } from 'react';
import _get from 'lodash/get';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { Table, FormDetailsItem, FormDetailsLink } from '../../common/components';

import { getWarranty, deleteWarranty, downloadWarranty } from '../services/warranty.service';
import { BREAKDOWN_STATUSES, DATE_FORMAT } from '../../app/config/config';
import { getBreakdowns } from '../../breakdown/services/breakdown.service';

const TABLE_CONFIG_BREAKDOWN = {
  columns: [
    {
      caption: 'Data utworzenia',
      dataField: 'createdAt',
      renderCell: (createdAt) => moment(createdAt).format(DATE_FORMAT),
      sort: true,
    },
    {
      caption: 'Miejscowość wystąpienia',
      dataField: 'incidentPlace',
    },
    {
      caption: 'Data wystąpienia awarii',
      dataField: 'date',
      renderCell: (date) => (date ? moment(date).format(DATE_FORMAT) : '-'),
      sort: true,
    },
    {
      caption: 'Status',
      dataField: 'status',
      renderCell: (status) => BREAKDOWN_STATUSES[status] || '',
    },
  ],
  noItemsFoundText: 'Brak awarii dla samochodu',
  initialSort: {
    sortField: 'createdAt',
    sortDirection: 'asc',
  },
  filterFields: 'incidentPlace',
};

class WarrantyDetails extends PureComponent {
  state = {
    data: {},
    breakdowns: [],
    isLoading: true,
  }

  id = this.props.match.params.id;

  async componentDidMount() {
    await this.getData();
  }

  getData = async () => {
    try {
      const { match: { params } } = this.props;

      const { data } = await getWarranty(params.id);

      this.setState({
        data,
        isLoading: false,
      });
    } catch {
      console.error('no data for route');
    }
  }

  getBreakdowns = async (requestParams) => {
    try {
      const { match: { params } } = this.props;

      this.setState({
        isLoading: true,
      });

      const { data: breakdowns } = await getBreakdowns({ warranty: params.id, ...requestParams });

      this.setState({
        breakdowns,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
      console.error(error);
    }
  }

  addBreakdown = () => {
    const { history } = this.props;

    history.push(`/breakdown?warranty=${this.id}`);
  }

  addBreakdownToCar = () => {
    const { history } = this.props;

    history.push(`/breakdown?warranty=${this.id}`);
  }

  onRemove = async () => {
    const { history } = this.props;

    if (window.confirm('Jesteś pewien, ze chcesz usunąć gwarancję?')) {
      await deleteWarranty(this.id);

      history.goBack();
    }
  }

  onEdit = () => {
    const { history } = this.props;

    history.push(`/warranty/${this.id}`);
  }

  onDownload = async () => {
    const { data } = this.state;
    const { match: { params } } = this.props;

    try {
      const vin = _get(data, 'car.vin', new Date().getDate);
      const dateFrom = moment(_get(data, 'dateFrom')).format(DATE_FORMAT);
      const dateTo = moment(_get(data, 'dateTo')).format(DATE_FORMAT);
      const pdfData = await downloadWarranty(params.id);

      const blob = new Blob([pdfData], {
        type: 'application/pdf',
      });
      saveAs(blob, `${vin}-${dateFrom}-${dateTo}.pdf`);
    } catch {
      toast.error('Brak danych w wybranym okresie');
    }
  }

  render() {
    const { isAdmin } = this.props;
    const { data, isLoading, breakdowns } = this.state;

    const options = _get(data, 'options', []).map((item) => `${_get(item, 'dictWarrantyOption.name')} |\n`);
    const dateClassName = moment(_get(data, 'dateTo')).isSameOrAfter(moment(), 'day') ? 'success' : 'warning';
    return (
      <>
        <div className="content">
          <div className="card">
            <div className="card-header row no-gutters align-items-center justify-content-between py-3">
              Szczegóły gwarancji
              <div>
                {isAdmin && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={this.onRemove}
                  >
                    Usuń gwarancję
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-primary ml-2"
                  onClick={this.onDownload}
                >
                  Pobierz gwarancję
                </button>
              </div>
            </div>
            <div className="card-body">
              <FormDetailsLink label="Firma" value={`${_get(data, 'car.company.name')} (NIP ${_get(data, 'car.company.vatId')})`} href={`/company-details/${_get(data, 'car.company.id')}`} />
              <FormDetailsItem label="Pakiet" value={_get(data, 'dictWarrantyType.name')} />
              {options.length > 0 && <FormDetailsItem label="Opcje dodatkowe" value={options} /> }
              <FormDetailsItem label="Data od" value={_get(data, 'dateFrom')} />
              <FormDetailsItem label="Data do" value={_get(data, 'dateTo')} className={dateClassName} />
              <FormDetailsItem label="Cena" value={`${_get(data, 'price')} PLN`} />
            </div>
          </div>
        </div>

        <div className="content">
          <div className="card">
            <div className="card-header">
              Szczegóły samochodu
            </div>
            <div className="card-body">
              <FormDetailsItem label="Marka" value={_get(data, 'car.manufacturer.name')} />
              <FormDetailsItem label="Model" value={_get(data, 'car.model.name')} />
              <FormDetailsLink label="VIN" value={_get(data, 'car.vin')} href={`/car-details/${_get(data, 'car.id')}`} />
              <FormDetailsItem label="Rok produkcji" value={_get(data, 'car.year')} />
              <FormDetailsItem label="Rodzaj paliwa" value={_get(data, 'car.fuelType.name')} />
              <FormDetailsItem label="Pojemność" value={`${_get(data, 'car.engine')} cm3`} />
              <FormDetailsItem label="Moc" value={`${_get(data, 'car.power')} kW | ${(_get(data, 'car.power') * 1.36).toFixed(0)} KM`} />
              <FormDetailsItem label="Przebieg" value={_get(data, 'car.mileage')} />
              {_get(data, 'car.notes') && <FormDetailsItem label="Dodatkowe informacje" value={_get(data, 'car.notes')} />}
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="content">
            <div className="card">
              <div className="card-header row no-gutters align-items-center justify-content-between py-3">
                Lista awarii
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={this.addBreakdown}
                >
                  Dodaj awarię
                </button>
              </div>
              <Table
                config={TABLE_CONFIG_BREAKDOWN}
                items={breakdowns}
                fetchData={this.getBreakdowns}
                isLoading={isLoading}
                pagination
                noBorders
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

WarrantyDetails.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
};

export default withRouter(WarrantyDetails);
